import { create } from 'zustand';
import { getAbsenceTypes } from '../api/generated/absence-types/absence-types';
import type { 
  FacilityFlowCoreModulesPlanningDtosAbsenceTypesAbsenceTypeDto,
  FacilityFlowCoreModulesPlanningDtosAbsenceTypesCreateAbsenceTypeRequest
} from '../api/generated/models';
import type { AbsenceTypeDto, CreateAbsenceTypeRequest } from '../types';
import { useToastStore } from './useToastStore';

interface AbsenceTypeState {
  absenceTypes: AbsenceTypeDto[];
  loading: boolean;
  error?: string;
  fetchAbsenceTypes: (includeInactive?: boolean) => Promise<void>;
  createAbsenceType: (request: CreateAbsenceTypeRequest) => Promise<AbsenceTypeDto>;
  getAbsenceTypeByCode: (code: string) => AbsenceTypeDto | undefined;
}

const absenceTypeApi = getAbsenceTypes();

export const useAbsenceTypeStore = create<AbsenceTypeState>((set, get) => ({
  absenceTypes: [],
  loading: false,
  error: undefined,

  fetchAbsenceTypes: async (includeInactive = false) => {
    set({ loading: true, error: undefined });
    try {
      const response = await absenceTypeApi.getApiV1AbsenceTypes({ includeInactive });
      
      const types: AbsenceTypeDto[] = (response || []).map((item: FacilityFlowCoreModulesPlanningDtosAbsenceTypesAbsenceTypeDto) => ({
        absenceTypeId: item.absenceTypeId || '',
        tenantId: item.tenantId || '',
        name: item.name || '',
        code: item.code || '',
        description: item.description || null,
        color: item.color || null,
        requiresApproval: item.requiresApproval || false,
        isActive: item.isActive ?? true,
        countsAgainstVacation: item.countsAgainstVacation ?? false,
        createdAt: item.createdAt || '',
        updatedAt: item.updatedAt || null,
      }));

      set({ absenceTypes: types, loading: false });
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Laden der Abwesenheitsarten';
      set({ error: errorMessage, loading: false });
      // Kein Toast beim Fetch - Error-State wird in der UI angezeigt
    }
  },

  createAbsenceType: async (request: CreateAbsenceTypeRequest) => {
    set({ loading: true, error: undefined });
    try {
      const createRequest: FacilityFlowCoreModulesPlanningDtosAbsenceTypesCreateAbsenceTypeRequest = {
        name: request.name,
        code: request.code,
        description: request.description || null,
        color: request.color || null,
        requiresApproval: request.requiresApproval,
      };
      const response = await absenceTypeApi.postApiV1AbsenceTypes(createRequest);

      const created: AbsenceTypeDto = {
        absenceTypeId: response.absenceTypeId || '',
        tenantId: response.tenantId || '',
        name: response.name || '',
        code: response.code || '',
        description: response.description || null,
        color: response.color || null,
        requiresApproval: response.requiresApproval || false,
        isActive: response.isActive || true,
        createdAt: response.createdAt || '',
        updatedAt: response.updatedAt || null,
      };

      set((state) => ({
        absenceTypes: [...state.absenceTypes, created],
        loading: false,
      }));

      useToastStore.getState().addToast(
        'Abwesenheitsart erfolgreich erstellt',
        'success'
      );

      return created;
    } catch (error) {
      const errorMessage = error instanceof Error ? error.message : 'Fehler beim Erstellen der Abwesenheitsart';
      set({ error: errorMessage, loading: false });
      useToastStore.getState().addToast(
        errorMessage,
        'error'
      );
      throw error;
    }
  },

  getAbsenceTypeByCode: (code: string) => {
    const { absenceTypes } = get();
    return absenceTypes.find((type) => type.code === code);
  },
}));
